import { Box, ButtonBase, Typography } from "@mui/material";
import type { BoardRelationshipKind } from "@chessla/shared";
import type { ReviewBoardSettings, ReviewBoardSettingsPatch } from "./review-board-settings";
import { relationshipEnabled, toggleRelationship } from "./review/position-markers";
import { POSITION_RELATIONSHIP_LEGEND } from "./review/teaching-explainer";
import { RelationshipGlyph } from "./relationship-evidence";

/** Each relationship color is a switch: off hides that kind from board arrows and badges. */
export const PositionRelationshipLegend = ({
  settings,
  onChange,
}: {
  settings: ReviewBoardSettings;
  onChange: (patch: ReviewBoardSettingsPatch) => void;
}) => (
  <Box
    component="section"
    aria-label="Board relationship colors"
    data-tour="Board relationship colors"
    sx={{ mt: 2.5 }}
  >
    <Typography component="h3" sx={{ fontSize: ".75rem", fontWeight: 950, textTransform: "uppercase", mb: 1 }}>
      Board relationship colors
    </Typography>
    <Box sx={{ display: "grid", gap: 0.5 }}>
      {Object.entries(POSITION_RELATIONSHIP_LEGEND).map(([key, { label, detail }]) => {
        const kind = key as BoardRelationshipKind;
        const enabled = relationshipEnabled(settings, kind);
        return (
          <ButtonBase
            key={kind}
            aria-pressed={enabled}
            aria-label={`${label}: ${enabled ? "shown" : "hidden"} on the board`}
            onClick={() => onChange(toggleRelationship(settings, kind))}
            sx={{
              display: "grid",
              gridTemplateColumns: "24px 1fr auto",
              columnGap: 1,
              alignItems: "center",
              textAlign: "left",
              p: 0.75,
              border: "1px solid",
              borderColor: enabled ? "divider" : "transparent",
              opacity: enabled ? 1 : 0.45,
              "&:hover": { bgcolor: "action.hover" },
              "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
          >
            <Box component="span" aria-hidden sx={{ width: 24, height: 24 }}>
              <RelationshipGlyph kind={kind} />
            </Box>
            <Typography
              component="span"
              sx={{ fontSize: ".75rem", fontWeight: 850, textDecoration: enabled ? "none" : "line-through" }}
            >
              {label}
            </Typography>
            <Typography component="span" sx={{ color: "text.secondary", fontSize: ".65rem", fontWeight: 900 }}>
              {enabled ? "ON" : "OFF"}
            </Typography>
            <Typography
              component="span"
              sx={{ gridColumn: "2 / 4", color: "text.secondary", fontSize: ".7rem", lineHeight: 1.5 }}
            >
              {detail}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
    <Typography sx={{ mt: 1, color: "text.secondary", fontSize: ".7rem", lineHeight: 1.5 }}>
      Tap a relationship to show or hide it on the board. Select a piece for support, x-rays, and discovered-attack
      potential, or enable All verified relationships. Alignments do not prove a winning tactic. Arrows are recalculated
      from the displayed position.
    </Typography>
  </Box>
);
